import { classNames } from 'libs/react';
import { useLayoutEffect } from 'libs/react';
import { useRef } from 'libs/react';
import { ResizeObserver } from '@juggle/resize-observer';
import { Reveal } from './private/Reveal';
import { React } from 'libs/react';
import './FormGroup.scss';
/**
 * @component FormGroup
 * @since 1.0.0
 */
export function FormGroup(props) {
    const { style, align, enter, leave } = Object.assign({ style: 'normal', align: true }, props);
    const node = useRef(null);
    const body = useRef(null);
    useLayoutEffect(() => {
        const element = body.current;
        if (element == null || align == false) {
            return;
        }
        let frame = 0;
        const schedule = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => alignHeads(element));
        };
        alignHeads(element);
        const resizeObserver = new ResizeObserver(schedule);
        const mutationObserver = new MutationObserver(() => {
            observeChildren();
            schedule();
        });
        const observeChildren = () => {
            resizeObserver.disconnect();
            resizeObserver.observe(element);
            for (const child of Array.from(element.children)) {
                resizeObserver.observe(child);
            }
        };
        observeChildren();
        mutationObserver.observe(element, {
            childList: true,
            subtree: true,
            characterData: true
        });
        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [align]);
    const className = classNames('form-group', 'form-group--' + style, props.className);
    return (React.createElement(Reveal, { enter: enter, leave: leave, presentClassName: "form-group--present", dismissClassName: "form-group--dismiss" },
        React.createElement("div", { ref: node, className: className },
            React.createElement("div", { ref: body, className: "form-group-body" }, props.children))));
}
/**
 * Pads the shorter field heads of each row so every field body starts at the
 * same height, e.g. when only one field of the row has infos.
 * @function alignHeads
 * @since 1.0.0
 */
function alignHeads(body) {
    var _a;
    const fields = [];
    for (const child of Array.from(body.children)) {
        const head = findHead(child, body);
        if (head) {
            head.style.paddingBottom = '';
            fields.push({ child, head });
        }
    }
    const rows = new Map();
    for (const { child, head } of fields) {
        const next = head.nextElementSibling;
        if (next == null) {
            continue;
        }
        const key = Math.round(child.offsetTop);
        const row = (_a = rows.get(key)) !== null && _a !== void 0 ? _a : [];
        row.push({ head, top: next.getBoundingClientRect().top });
        rows.set(key, row);
    }
    for (const row of Array.from(rows.values())) {
        if (row.length < 2) {
            continue;
        }
        const max = Math.max(...row.map(item => item.top));
        for (const { head, top } of row) {
            const delta = Math.round(max - top);
            if (delta > 0) {
                head.style.paddingBottom = delta + 'px';
            }
        }
    }
}
/**
 * Finds the head of the field rendered in a group child, skipping the heads
 * of nested groups.
 * @function findHead
 * @since 1.0.0
 */
function findHead(child, body) {
    const candidates = child.querySelectorAll('[class*="-field-head"]');
    for (const candidate of Array.from(candidates)) {
        const isHead = Array.from(candidate.classList).some(name => name.endsWith('-field-head'));
        if (isHead) {
            return candidate.closest('.form-group-body') == body ? candidate : null;
        }
    }
    return null;
}
